// ===========================================
// BUSTED! BLACKJACK - USE RESYNC HOOK
// ===========================================
// Unified resync mechanism for all sync scenarios:
// - Tab visibility (hidden for 5+ seconds)
// - Network reconnection
// - Page refresh
// - Round boundary checkpoint
// ===========================================

import { useCallback, useRef } from 'react';
import { useSocketStore } from '../stores';
import { useGameStateStore } from '../stores/gameStateStore';
import { getClientTableMaster } from '../utils/ClientTableMaster';
import { GamePhase } from '@busted-blackjack/shared';
import type { JoinTableMessage } from '@busted-blackjack/shared';

export interface ResyncOptions {
  /** Reason for resync (for logging) */
  reason: 'visibility' | 'reconnect' | 'refresh' | 'round_boundary' | 'manual';
}

export interface UseResyncReturn {
  /** Trigger a resync - clears state and requests fresh state from server */
  resync: (options: ResyncOptions) => void;
  /** Check if a resync is currently in progress */
  isResyncing: boolean;
}

/**
 * Hook for unified state resync across all scenarios.
 *
 * Flow:
 * 1. Enter CTM away mode (pause event processing)
 * 2. Clear dynamic game state (cards, chips, scores - preserves tableId, etc.)
 * 3. Send join_table to server (more robust than request_sync - survives socket issues)
 * 4. Server responds with table_joined
 * 5. useGameEvents processes table_joined via syncFromTableState + triggers fullVisualResync
 * 6. Exit CTM away mode (resume normal processing)
 *
 * Note: We use join_table instead of request_sync because browsers may kill/throttle
 * websockets when tabs are hidden. join_table ensures proper reconnection.
 */
export function useResync(): UseResyncReturn {
  const sendMessage = useSocketStore((state) => state.sendMessage);
  const clearGameStateForResync = useGameStateStore((state) => state.clearGameStateForResync);

  // Track resync state
  const isResyncingRef = useRef(false);
  const resyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resync = useCallback((options: ResyncOptions) => {
    // Prevent concurrent resyncs
    if (isResyncingRef.current) {
      console.log('[useResync] Resync already in progress, ignoring');
      return;
    }

    const { tableId } = useGameStateStore.getState();
    if (!tableId) {
      console.log('[useResync] No table ID, skipping resync');
      return;
    }

    console.log(`[useResync] Starting resync - reason: ${options.reason}`);
    isResyncingRef.current = true;

    // Step 1: Enter away mode (pause event processing)
    const ctm = getClientTableMaster();
    ctm.enterAwayMode();

    // Step 2: Clear dynamic game state
    // This preserves: tableId, tableName, mySeatPosition, isConnected, sidebets
    // This clears: players, dealer, cards, scores, timers, etc.
    clearGameStateForResync();
    console.log('[useResync] Cleared dynamic state');

    // Step 3: Send join_table to server (more robust than request_sync)
    // This triggers a full reconnection flow and survives socket throttling
    const joinMessage: JoinTableMessage = { type: 'join_table', tableId, mode: 'play' };
    sendMessage(joinMessage);
    console.log(`[useResync] Sent join_table to server for table ${tableId}`);

    // Step 4: Wait for table_joined (processed by useGameEvents)
    // Set up a one-time subscription to detect when state is populated
    // Note: table_joined also triggers fullVisualResync automatically
    const unsubscribe = useGameStateStore.subscribe((state, prevState) => {
      // Check if we received new player data (indicates table_joined was processed)
      // We check for phase change or players change as indicators
      const hasNewData =
        state.players.size > 0 ||
        state.dealer.cards.length > 0 ||
        (state.phase !== GamePhase.WAITING && state.phase !== prevState.phase);

      if (hasNewData && isResyncingRef.current) {
        console.log('[useResync] State populated, completing resync');

        // Step 5: Exit away mode with new state
        const ctm = getClientTableMaster();
        ctm.exitAwayMode(state.round?.roundNumber ?? 0, state.phase);

        // Cleanup
        isResyncingRef.current = false;
        unsubscribe();

        if (resyncTimeoutRef.current) {
          clearTimeout(resyncTimeoutRef.current);
          resyncTimeoutRef.current = null;
        }

        console.log('[useResync] Resync complete');
      }
    });

    // Safety timeout - exit away mode after 10 seconds even if no table_joined received
    // This prevents getting stuck in away mode if server doesn't respond
    resyncTimeoutRef.current = setTimeout(() => {
      if (isResyncingRef.current) {
        console.warn('[useResync] Resync timeout - forcing exit from away mode');
        const ctm = getClientTableMaster();
        const state = useGameStateStore.getState();
        ctm.exitAwayMode(state.round?.roundNumber ?? 0, state.phase);
        isResyncingRef.current = false;
        unsubscribe();
      }
    }, 10000);

  }, [sendMessage, clearGameStateForResync]);

  return {
    resync,
    get isResyncing() {
      return isResyncingRef.current;
    },
  };
}

export default useResync;
